/**
 * @file membership.schema.ts
 * @description Vínculo: a relação `conta × marca × papel`.
 *
 * ## O problema
 *
 * `role` era o discriminante da união em `users`, então uma pessoa tinha
 * exatamente um papel — e para ter dois, precisava de duas contas. O sistema já
 * contornava isso de duas formas, ambas caras:
 *
 *   - `adminLogin` elevava contas `@evasaude.com.br` a uma sessão `INTERNAL` +
 *     `SUDO` **sem alterar o documento**, para que uma conta só acessasse o app
 *     e o painel. A elevação tinha de ser reproduzida no refresh, e vazava para
 *     três pontos do código.
 *   - `switchProviderContext` procurava **outro documento de usuário** com o
 *     mesmo `medicalProviderId` e outro `partnerId`, e emitia tokens dele. Ou
 *     seja: um profissional em três marcas era três contas, com três senhas.
 *
 * As duas são o mesmo conceito implementado à mão. Aqui ele vira dado.
 *
 * ## O desenho é aditivo de propósito
 *
 * `users` continua sendo a conta (e-mail, senha, verificação). O vínculo é uma
 * coleção nova. Durante a janela de migração, uma conta sem vínculo gravado tem
 * o vínculo **derivado** do próprio documento (`deriveMembershipsFromUser`), de
 * modo que nenhum login quebra antes do backfill rodar. É o expand do
 * AGENTS.md aplicado a identidade.
 */

import { z } from 'zod';
import { marcaDoVinculo } from './brand';

/**
 * Papel de uma conta dentro de uma marca.
 *
 * Vocabulário novo, alinhado ao modelo de marcas. O mapa a partir dos
 * `role`/`permissionsPresets` antigos está em `LEGACY_ROLE_TO_BRAND_ROLE` e em
 * `deriveMembershipsFromUser`.
 */
export const BrandRoleEnum = z.enum([
  /**
   * Mantenedor do sistema: governa marcas, funcionalidades e concessões.
   * É o antigo `INTERNAL` + `SUDO`/`DEV`, sempre na marca Eva.
   */
  'MAINTAINER',
  /** Time administrativo da marca. Antigo preset `PARTNER_ADMIN`. */
  'BRAND_ADMIN',
  /** Atendimento/CRM da marca. Antigo `PARTNER_CRM` (e `CRM` na Eva). */
  'BRAND_CRM',
  /** Curadoria de conteúdo da marca. Antigo `PARTNER_CMS` (e `CMS` na Eva). */
  'BRAND_CMS',
  /** Profissional de saúde que atende pelos serviços da marca. */
  'PROVIDER',
  /** Gestor de uma empresa da carteira da marca. Exige `companyId`. */
  'COMPANY_MANAGER',
  /** Paciente/colaboradora. Antigo `PREGNANT_WOMAN`. */
  'PATIENT',
  /** Acompanhante com acesso compartilhado. Antigo `GUEST`. */
  'GUEST',
]);
export type BrandRole = z.infer<typeof BrandRoleEnum>;

/** Papéis que dão acesso ao admin da marca. */
export const ADMIN_BRAND_ROLES: BrandRole[] = [
  'MAINTAINER',
  'BRAND_ADMIN',
  'BRAND_CRM',
  'BRAND_CMS',
  'COMPANY_MANAGER',
];

/**
 * A equipe administrativa de UMA marca — o que o mantenedor nomeia no
 * portal `/mantenedor`. Não inclui MAINTAINER (governa o ecossistema, não uma
 * marca) nem COMPANY_MANAGER (é da empresa, e quem o nomeia é a marca).
 */
export const BRAND_TEAM_ROLES: BrandRole[] = ['BRAND_ADMIN', 'BRAND_CRM', 'BRAND_CMS'];

/**
 * `PENDING_APPROVAL` (A037): a profissional entrou por link de convite com
 * validação ligada. A conta existe, ela entra, mas o vínculo não vale para
 * atender nem para a rede — até uma administradora da marca aprovar.
 * Enum se alarga, nunca se restringe.
 */
export const MembershipStatusEnum = z.enum(['ACTIVE', 'SUSPENDED', 'PENDING_APPROVAL']);
export type MembershipStatus = z.infer<typeof MembershipStatusEnum>;

/**
 * Preprocessamento do vínculo inteiro: a marca depende do papel (A044).
 * `MAINTAINER` fica sem marca; o resto normaliza como `brandIdField`.
 */
const comMarcaDoVinculo = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') return raw;
  const v = raw as Record<string, unknown>;
  return { ...v, brandId: marcaDoVinculo(v.role, v.brandId) };
};

export const MembershipSchema = z.preprocess(comMarcaDoVinculo, z.object({
  id: z.string(),

  /** A conta. Referencia o campo `id` de `users` — não o id do documento. */
  accountId: z.string(),

  /**
   * A marca onde este papel vale. `null` só para `MAINTAINER`, que governa o
   * ecossistema e não mora em marca nenhuma (A044).
   */
  brandId: z.string().min(1).nullable(),

  role: BrandRoleEnum,

  /**
   * Empresa, quando o papel é da camada de empresa.
   *
   * Obrigatório para `COMPANY_MANAGER` (ver `.superRefine`); opcional para
   * `PATIENT`, onde indica que a paciente é colaboradora daquela empresa.
   */
  companyId: z.string().nullable().default(null),

  /**
   * Perfil profissional, quando o papel é `PROVIDER`.
   *
   * É a chave que torna o vínculo capaz de substituir
   * `switchProviderContext`: o mesmo `medicalProviderId` em N vínculos, uma
   * conta só.
   */
  medicalProviderId: z.string().nullable().default(null),

  status: MembershipStatusEnum.default('ACTIVE'),

  /**
   * Como o vínculo nasceu (A037). `CONVITE` = alguém da marca escolheu esta
   * pessoa pelo e-mail; `LINK` = ela chegou por um link aberto. Ausente em
   * vínculo anterior a isto (backfill, nomeação, cadastro).
   */
  /**
   * Como o vínculo nasceu. `CONVITE`/`LINK`: profissional (A034/A037).
   * `VINCULO`: paciente que já tinha conta em outra marca e se juntou a esta
   * pelo cadastro ou pelo convite de paciente (A052).
   */
  origem: z.enum(['CONVITE', 'LINK', 'VINCULO']).optional(),

  /**
   * Validação da profissional pela marca (A037). Quem foi convidada pelo
   * e-mail já nasce validada por quem convidou. Quem entrou por link sem
   * validação fica com `null` até uma administradora validar — e enquanto a
   * marca tiver profissional não validada, a rede dela não participa de
   * concessão. Só faz sentido em `role: PROVIDER`.
   */
  validadoEm: z.coerce.date().nullable().optional(),
  validadoPor: z.string().nullable().optional(),

  /**
   * E-mail da empresa, confirmado por código no link validado (A062). Prova
   * que a paciente é colaboradora; nunca é login — a conta usa o e-mail que
   * ela escolheu. Fica no vínculo porque é da relação com a empresa, e some
   * com ela.
   */
  emailCorporativo: z.string().email().nullable().optional(),
  emailCorporativoValidadoEm: z.coerce.date().nullable().optional(),

  /**
   * Dependente (A062, fase E): a pessoa entrou convidada por uma colaboradora
   * — o funcionário que convida a esposa grávida. Tem conta própria, ocupa
   * vaga e é contada à parte; ninguém vê nada de ninguém por causa do
   * convite. Ausente = titular (toda paciente de antes).
   */
  tipo: z.enum(['TITULAR', 'DEPENDENTE']).optional(),
  /** Quem convidou o dependente (`accountId` da titular). Só registro; não dá acesso a nada. */
  convidadoPor: z.string().nullable().optional(),

  /**
   * Vínculo derivado do documento legado de usuário, ainda não gravado pelo
   * backfill. Marcado para que o script saiba o que falta e para que uma
   * inconsistência entre derivado e gravado seja diagnosticável.
   */
  derived: z.boolean().default(false),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
}).superRefine((m, ctx) => {
  if (m.role !== 'MAINTAINER' && !m.brandId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['brandId'],
      message: 'Todo papel que não é mantenedor vale numa marca.',
    });
  }
  if (m.role === 'COMPANY_MANAGER' && !m.companyId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['companyId'],
      message: 'Gestor de empresa exige companyId — o papel não existe fora de uma empresa.',
    });
  }
  /*
   * O vínculo de profissional NÃO exige o perfil (A033/A034).
   *
   * Havia aqui uma regra exigindo `medicalProviderId` para o papel `PROVIDER`.
   * Ela fazia sentido quando o perfil era a identidade da profissional entre
   * marcas — era ele que substituía o `switchProviderContext`. Com o perfil
   * dentro da conta, quem identifica é o `accountId`, e profissional convidada
   * hoje nasce sem documento separado nenhum.
   *
   * O campo continua existindo e sendo gravado para os vínculos antigos: é a
   * ligação com o cadastro legado enquanto `medicalProviders` existir.
   */
}));

export type Membership = z.infer<typeof MembershipSchema>;

/**
 * Contexto escolhido para a sessão: em qual marca, com qual papel.
 *
 * Vai no JWT. Antes, o token carregava `role` + `partnerId` soltos, o que
 * tornava impossível uma conta ter dois papéis — o token não tinha onde
 * expressar a escolha.
 */
export const SessionContextSchema = z.preprocess(comMarcaDoVinculo, z.object({
  /** `null` no contexto de mantenedora (A044). */
  brandId: z.string().min(1).nullable(),
  role: BrandRoleEnum,
  companyId: z.string().nullable().optional(),
  medicalProviderId: z.string().nullable().optional(),
  /** Vínculo aguardando aprovação da marca (A037): entra, mas não atende. */
  pendente: z.boolean().optional(),
}));
export type SessionContext = z.infer<typeof SessionContextSchema>;

/**
 * Tradução do preset de permissão antigo para o papel de marca.
 *
 * `SUDO` e `DEV` viram `MAINTAINER` porque é isso que eram na prática: acesso
 * irrestrito, sempre em contas da equipe Eva.
 */
export const LEGACY_PRESET_TO_BRAND_ROLE: Record<string, BrandRole> = {
  SUDO: 'MAINTAINER',
  DEV: 'MAINTAINER',
  CRM: 'BRAND_CRM',
  CMS: 'BRAND_CMS',
  PARTNER_ADMIN: 'BRAND_ADMIN',
  PARTNER_CRM: 'BRAND_CRM',
  PARTNER_CMS: 'BRAND_CMS',
};

/**
 * Tradução inversa: papel de marca → `role` + presets do modelo antigo.
 *
 * É a ponte de compatibilidade da fase 3. O token novo carrega o contexto, mas
 * continua carregando `role` e `permissionsPresets` no formato que
 * `authorize()` e `hasPermission()` já entendem — senão trocar de contexto
 * exigiria reescrever toda a autorização no mesmo passo, que é exatamente o
 * big bang que o plano evita.
 */
export function brandRoleToLegacy(role: BrandRole): {
  role: 'PREGNANT_WOMAN' | 'GUEST' | 'INTERNAL' | 'PROVIDER';
  permissionsPresets: string[];
} {
  switch (role) {
    case 'MAINTAINER':
      return { role: 'INTERNAL', permissionsPresets: ['SUDO'] };
    case 'BRAND_ADMIN':
      return { role: 'INTERNAL', permissionsPresets: ['PARTNER_ADMIN'] };
    case 'BRAND_CRM':
      return { role: 'INTERNAL', permissionsPresets: ['PARTNER_CRM'] };
    case 'BRAND_CMS':
      return { role: 'INTERNAL', permissionsPresets: ['PARTNER_CMS'] };
    case 'COMPANY_MANAGER':
      return { role: 'INTERNAL', permissionsPresets: ['COMPANY_MANAGER'] };
    case 'PROVIDER':
      return { role: 'PROVIDER', permissionsPresets: [] };
    case 'PATIENT':
      return { role: 'PREGNANT_WOMAN', permissionsPresets: [] };
    case 'GUEST':
      return { role: 'GUEST', permissionsPresets: [] };
  }
}

/** Ordem de precedência quando uma conta tem vários papéis na mesma marca. */
const PRECEDENCIA: BrandRole[] = [
  'MAINTAINER',
  'BRAND_ADMIN',
  'BRAND_CRM',
  'BRAND_CMS',
  'COMPANY_MANAGER',
  'PROVIDER',
  'PATIENT',
  'GUEST',
];

/**
 * A ordem em que os contextos aparecem na tela de escolha.
 *
 * NÃO é a `PRECEDENCIA` acima, e a diferença é deliberada: aquela responde
 * "qual papel manda" e serve para escolher um contexto sozinho; esta responde
 * "por onde eu entro hoje", que é a pergunta da tela.
 *
 * `PATIENT` vem primeiro porque desde A032 a mesma conta pode ser paciente e
 * administradora, e para essa pessoa o cadastro de paciente costuma ser o que
 * ela mais usa. Ordenar pelo papel mais poderoso otimizaria para a hierarquia
 * em vez do uso.
 *
 * O resto segue a precedência, sem inventar ranking de uso para papéis sobre
 * os quais não temos evidência.
 */
export const ORDEM_NA_TELA_DE_CONTEXTO: BrandRole[] = [
  'PATIENT',
  ...PRECEDENCIA.filter((r) => r !== 'PATIENT'),
];

/** Posição do papel na tela de escolha. Papel desconhecido vai para o fim. */
export function ordemDeExibicao(role: BrandRole): number {
  const i = ORDEM_NA_TELA_DE_CONTEXTO.indexOf(role);
  return i === -1 ? ORDEM_NA_TELA_DE_CONTEXTO.length : i;
}

/**
 * O papel mais forte de uma lista — o que o admin usa para decidir a tela
 * inicial quando a conta tem mais de um vínculo na mesma marca.
 */
export function strongestRole(roles: BrandRole[]): BrandRole | null {
  for (const role of PRECEDENCIA) {
    if (roles.includes(role)) return role;
  }
  return null;
}
